import { getRouteApi } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import CancelIcon from '@/assets/icons/cancel.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import LinkText from '@/components/LinkText/LinkText'
import Table, {
  type Column,
  type TableProps,
  type TableAction
} from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import TableTopBarActions from '@/components/TableTopBar/TableTopBarActions'
import NotApplicable from '@/components/common/NotApplicable/NotApplicable'
import type useRowSelection from '@/hooks/useRowSelection'
import type { IdAndName } from '@/types/id-and-name'
import { getColumnWidth } from '@/utils/get-column-width'

import SectionStatusTag from '../components/SectionStatusTag'
import type { SectionStatus } from '../constants/section-status'
import type { SectionsList, SectionsListItem } from '../queries/useSections'
import { formatNumberRanges } from '../utils/format-number-ranges'

type SectionsListTableProps = {
  columns: Column<SectionsListItem>[]
  rowSelection: ReturnType<typeof useRowSelection>
  data?: SectionsList
  onRemoveClick: (section: SectionsListItem) => void
  onBulkRemoveClick: (sectionsIds: string[]) => void
} & Pick<
  TableProps<SectionsListItem>,
  | 'count'
  | 'totalCount'
  | 'pagination'
  | 'isLoading'
  | 'onChangePage'
  | 'onChangePageSize'
  | 'sorting'
  | 'onChangeSorting'
>

export const getSectionsListColumns = (
  t: TFunction<'sections'>,
  isExtraLarge: boolean
): Column<SectionsListItem>[] => [
  {
    dataIndex: 'name',
    key: 'name',
    sorterKey: 'name',
    label: t('label.name'),
    render: (name: string, { id }) => (
      <LinkText
        to="/sections/$sectionId"
        params={{
          sectionId: id
        }}
      >
        {name}
      </LinkText>
    )
  },
  {
    dataIndex: 'course',
    key: 'course',
    sorterKey: 'course',
    label: t('label.course'),
    render: (course: { id: string; displayName: string }) => (
      <LinkText
        to="/courses-management/$courseId"
        params={{
          courseId: course.id
        }}
      >
        {course.displayName}
      </LinkText>
    )
  },
  {
    dataIndex: 'curriculum',
    key: 'curriculum',
    sorterKey: 'curriculum',
    label: t('label.curriculum'),
    render: (curriculum: IdAndName) => (
      <LinkText
        to="/curricula/$curriculumId"
        params={{
          curriculumId: curriculum.id
        }}
      >
        {curriculum.name}
      </LinkText>
    )
  },
  {
    dataIndex: 'semesters',
    key: 'semesters',
    width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
    label: t('label.semesters'),
    render: (semesters: number[]) =>
      formatNumberRanges(semesters) || <NotApplicable />
  },
  {
    dataIndex: 'studentsCount',
    key: 'studentsCount',
    sorterKey: 'students_count',
    width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
    label: t('label.students-number')
  },
  {
    dataIndex: 'status',
    key: 'status',
    sorterKey: 'status',
    width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
    label: t('label.status'),
    render: (status: SectionStatus) => <SectionStatusTag status={status} />
  }
]

const routeApi = getRouteApi('/_auth/sections/list')

const SectionsListTable = (props: SectionsListTableProps) => {
  const navigate = routeApi.useNavigate()

  const { t } = useTranslation('sections')

  const actions: TableAction<SectionsListItem>[] = [
    {
      icon: <EditIcon />,
      text: t('button.edit'),
      onClick: row =>
        navigate({
          to: '/sections/$sectionId/edit',
          params: { sectionId: row.id }
        }),
      isHidden: section => !section.canBeEdited
    },
    {
      icon: <CancelIcon />,
      onClick: props.onRemoveClick,
      text: t('button.remove'),
      variantAction: 'danger'
    }
  ]

  const bulkActions = [
    {
      text: t('button.remove'),
      icon: <DeleteIcon />,
      onClick: () => props.onBulkRemoveClick(props.rowSelection.selectedItems)
    }
  ]

  return (
    <Table
      id="sections-table"
      data={props.data ?? []}
      columns={props.columns}
      count={props.count}
      totalCount={props.totalCount}
      actions={actions}
      rightTopBar={
        <TableTopBarActions
          selectedItemsLength={props.rowSelection.selectedItems.length}
          actions={bulkActions}
        />
      }
      rowSelection={{
        selectedItems: props.rowSelection.selectedItems,
        allCheckboxValue: props.rowSelection.getSelectAllCheckboxValue(),
        count: props.count ?? 0,
        labelHeader: selected =>
          t(
            selected
              ? 'label.deselect-all-sections'
              : 'label.select-all-sections'
          ),
        labelItem: ({ selected, name }) =>
          t(selected ? 'label.deselect-section' : 'label.select-section', {
            NAME: name
          }),
        onSelect: props.rowSelection.onSelect,
        onSelectAll: props.rowSelection.onSelectAll
      }}
      pagination={props.pagination}
      isLoading={props.isLoading}
      onChangePage={props.onChangePage}
      onChangePageSize={props.onChangePageSize}
      sorting={props.sorting}
      onChangeSorting={props.onChangeSorting}
      caption={t('header.sections')}
    />
  )
}

export default memo(SectionsListTable)
